import { v5 as uuidv5 } from 'uuid';
import { proposalHref } from '../proposalHref';
/** Where the user is standing, from a pathname (assistOrigin or location). Query is kept. */ export function parseChatOrigin(pathname) {
    if (!pathname) return {
        surface: 'root'
    };
    const qIndex = pathname.indexOf('?');
    const path = qIndex >= 0 ? pathname.slice(0, qIndex) : pathname;
    const search = qIndex >= 0 ? pathname.slice(qIndex + 1) : '';
    const query = new URLSearchParams(search);
    const focus = parseChatFocus(path, query);
    const program = path.match(/^\/delivery\/programs\/([^/]+)/);
    if (program?.[1]) {
        return {
            surface: 'delivery_program',
            programId: program[1],
            ...focus
        };
    }
    // Command-center date drawer: ?occ=&in=<programId>
    const commandCenterProgram = query.get('in')?.trim();
    if (path.startsWith('/delivery/command-center') && commandCenterProgram) {
        return {
            surface: 'delivery_program',
            programId: commandCenterProgram,
            ...focus
        };
    }
    if (path === '/delivery' || path.startsWith('/delivery/')) {
        return {
            surface: 'delivery_home',
            ...focus
        };
    }
    const capture = path.match(/^\/capture\/([^/]+)/);
    if (capture?.[1]) return {
        surface: 'capture_pursuit',
        pursuitId: capture[1],
        ...focus
    };
    const proposal = path.match(/^\/proposal\/([^/]+)/);
    if (proposal?.[1]) return {
        surface: 'proposal',
        projectId: proposal[1],
        ...focus
    };
    const project = path.match(/^\/project\/([^/]+)/);
    if (project?.[1]) return {
        surface: 'assistant',
        projectId: project[1],
        ...focus
    };
    if (path.startsWith('/assistant')) return {
        surface: 'assistant',
        ...focus
    };
    return {
        surface: 'root',
        ...focus
    };
}
/** Drawer / page query ids so "comment on this" is the open record. */ export function parseChatFocus(path, query) {
    const focus = {};
    const dutyPage = path.match(/^\/delivery\/programs\/[^/]+\/deliverables\/([^/]+)/);
    if (dutyPage?.[1]) focus.dutyId = dutyPage[1];
    const occ = query.get('occ')?.trim();
    if (occ) focus.occurrenceId = occ;
    const submit = query.get('submit')?.trim();
    if (submit && submit !== 'new' && !focus.occurrenceId) focus.occurrenceId = submit;
    const review = query.get('review')?.trim();
    if (review) focus.submissionId = review;
    const forDuty = query.get('forDuty')?.trim();
    if (forDuty && !focus.dutyId) focus.dutyId = forDuty;
    const assign = query.get('assign')?.trim();
    if (assign && !focus.dutyId) {
        const parts = assign.split(':');
        const duty = parts[2]?.trim();
        if (duty && duty !== 'new') focus.dutyId = duty;
    }
    const doc = query.get('doc')?.trim();
    if (doc) focus.documentId = doc;
    const item = query.get('item')?.trim();
    if (item) focus.reviewItemId = item;
    const issue = query.get('issue')?.trim();
    if (issue) focus.issueId = issue;
    const wp = (query.get('wp') ?? query.get('workProduct'))?.trim();
    if (wp) focus.workProductId = wp;
    const comment = query.get('comment')?.trim();
    if (comment) focus.commentId = comment;
    const artifact = query.get('artifact')?.trim();
    if (artifact) focus.artifactId = artifact;
    return focus;
}
/**
 * "Open in full view" from the Assist rail. Capture stays on the pursuit
 * (not /assistant). Programs go to workroom; proposal/project to that workspace.
 */ function decodeSegment(segment) {
    try {
        return decodeURIComponent(segment);
    } catch  {
        return segment;
    }
}
export function expandDestination(origin, conversationId) {
    if (conversationId) {
        const id = conversationId.startsWith('shanks-') ? conversationId : `shanks-${conversationId}`;
        const program = origin?.match(/^\/delivery\/programs\/([^/?]+)/);
        return program?.[1] ? `/delivery/programs/${program[1]}/workroom?chat=${encodeURIComponent(id)}` : `/assistant/${encodeURIComponent(id)}`;
    }
    if (!origin) return '/assistant';
    const path = origin.split('?')[0] ?? origin;
    const program = path.match(/^\/delivery\/programs\/([^/]+)/);
    if (program?.[1]) return `/delivery/programs/${program[1]}/workroom`;
    const capture = path.match(/^\/capture\/([^/]+)/);
    if (capture?.[1]) return `/capture/${capture[1]}`;
    const project = path.match(/^\/project\/([^/]+)/);
    if (project?.[1]) return `/project/${project[1]}`;
    const proposal = path.match(/^\/proposal\/([^/]+)/);
    if (proposal?.[1]) return proposalHref(decodeSegment(proposal[1]));
    return '/assistant';
}
/** Stable, database-sized Assist identity for (user, surface, primary bind). */ export function assistConversationId(envelope, userId) {
    const bind = envelope.programId || envelope.pursuitId || envelope.projectId || 'none';
    // Keep existing keys that could be persisted in Pangaea's varchar(36).
    const who = (userId ?? '').replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 64) || 'pending';
    const legacy = `assist-${envelope.surface}-${bind}-${who}`;
    if (legacy.length <= 36) return legacy;
    return uuidv5(JSON.stringify([
        'specra-assist',
        envelope.surface,
        bind,
        userId || 'pending'
    ]), uuidv5.URL);
}
/** Short composer/header chip. Empty on root / unpinned assistant. */ export function envelopeChip(envelope) {
    switch(envelope.surface){
        case 'delivery_program':
            return 'Program';
        case 'capture_pursuit':
            return 'Pursuit';
        case 'proposal':
        case 'proposal_document':
            return 'Proposal';
        case 'rfi_response':
            return 'RFI';
        case 'delivery_home':
            return 'Delivery';
        default:
            return '';
    }
}
/** JSON body for the SSE bridge (snake_case, matches express app.ts). */ export function envelopeToJson(envelope) {
    const out = {
        surface: envelope.surface
    };
    if (envelope.pursuitId) out.pursuit_id = envelope.pursuitId;
    if (envelope.projectId) out.project_id = envelope.projectId;
    if (envelope.programId) out.program_id = envelope.programId;
    if (envelope.documentId) out.document_id = envelope.documentId;
    if (envelope.sectionAnchor) out.section_anchor = envelope.sectionAnchor;
    if (envelope.responseContainer) out.response_container_json = JSON.stringify(envelope.responseContainer);
    if (envelope.volumeId) out.volume_id = envelope.volumeId;
    if (envelope.occurrenceId) out.occurrence_id = envelope.occurrenceId;
    if (envelope.dutyId) out.duty_id = envelope.dutyId;
    if (envelope.workProductId) out.work_product_id = envelope.workProductId;
    if (envelope.issueId) out.issue_id = envelope.issueId;
    if (envelope.commentId) out.comment_id = envelope.commentId;
    if (envelope.reviewItemId) out.review_item_id = envelope.reviewItemId;
    if (envelope.artifactId) out.artifact_id = envelope.artifactId;
    if (envelope.submissionId) out.submission_id = envelope.submissionId;
    return out;
}
/** Fields for `new AgentTypes.ChatContext(...)`. */ export function toChatContextInit(envelope) {
    return {
        surface: envelope.surface,
        pursuitId: envelope.pursuitId ?? '',
        projectId: envelope.projectId ?? '',
        programId: envelope.programId ?? '',
        documentId: envelope.documentId ?? '',
        volumeId: envelope.volumeId ?? '',
        occurrenceId: envelope.occurrenceId ?? '',
        dutyId: envelope.dutyId ?? '',
        workProductId: envelope.workProductId ?? '',
        issueId: envelope.issueId ?? '',
        commentId: envelope.commentId ?? '',
        reviewItemId: envelope.reviewItemId ?? '',
        artifactId: envelope.artifactId ?? '',
        submissionId: envelope.submissionId ?? '',
        sectionAnchor: envelope.sectionAnchor ?? '',
        responseContainerJson: envelope.responseContainer ? JSON.stringify(envelope.responseContainer) : '',
        pins: (envelope.pins ?? []).map((p)=>({
                surface: p.surface,
                pursuitId: p.pursuitId ?? '',
                projectId: p.projectId ?? '',
                programId: p.programId ?? '',
                documentId: p.documentId ?? '',
                volumeId: p.volumeId ?? ''
            }))
    };
}
export const MAX_CHAT_PINS = 8;
/** /assistant owner-only private thread with no project bind. */ export function canShowPinPicker(opts) {
    if (!opts.pathname.startsWith('/assistant')) return false;
    if (opts.projectId) return false;
    if (!opts.isOwner || opts.isShared) return false;
    return true;
}
export function pinKey(pin) {
    return [
        pin.surface,
        pin.pursuitId,
        pin.projectId,
        pin.programId,
        pin.documentId,
        pin.volumeId
    ].filter(Boolean).join(':');
}
/**
 * Prefer the live path when it is a known desk; otherwise keep the origin
 * the rail was opened from (user navigated to a root-ish page with Assist open).
 */ export function resolveAssistPath(pathname, origin) {
    const live = parseChatOrigin(pathname);
    if (live.surface !== 'root') return pathname ?? '';
    return origin || pathname || '';
}
